import { fetchCompare, type CompareBody } from '../api/compare'
import { fetchModels } from '../api/models'

/**
 * What the meteogram's chart and its table are both looking at.
 *
 * They are two views of one answer — a chart and the same numbers — so they
 * share one fetch and one selection rather than each asking `/api/compare` for
 * themselves. `ensureCompare` dedupes by (point, field, catalog revision), so
 * either panel alone works and both together are still one request.
 *
 * Which series are *hidden* lives here too, because the legend is the control:
 * switching a model off has to take it out of the chart and out of the blend's
 * *n* at the same time, and those are in two different components.
 */

export type CompareStatus = 'idle' | 'loading' | 'ready' | 'error'

/**
 * Which of the two views the Meteogram panel shows. Since Stage 10.5 the
 * table is not a panel of its own: it is the chart's accessible form, one
 * switch away inside the same panel, with the same chips and window.
 */
export type CompareView = 'chart' | 'table'

export const compare = $state({
  /** The field id being charted. A catalog field, not a registry constant. */
  field: 't2m',
  body: null as CompareBody | null,
  status: 'idle' as CompareStatus,
  error: null as string | null,
  /** Series ids switched off in the legend. */
  hidden: [] as string[],
  /** Field ids the catalog reports for at least one model, with their labels. */
  fields: [] as { id: string; label: string }[],
  view: 'chart' as CompareView,
})

export function setCompareView(view: CompareView): void {
  compare.view = view
}

export function isHidden(id: string): boolean {
  return compare.hidden.includes(id)
}

export function toggleSeries(id: string): void {
  compare.hidden = isHidden(id)
    ? compare.hidden.filter((other) => other !== id)
    : [...compare.hidden, id]
}

export function setCompareField(id: string): void {
  if (compare.field === id) return
  compare.field = id
  // A hidden model is a statement about *this* comparison, not a preference:
  // carrying it to another field would silently drop a line somebody never
  // switched off.
  compare.hidden = []
}

/**
 * The chartable fields, from the catalog rather than from a second table.
 *
 * A field a deployment does not ingest is absent from the catalog and gets no
 * chip — the same quiet degradation as "no model data at all". The order is
 * the catalog's own, which is the registry's, so two deployments show their
 * shared fields in the same order.
 */
export async function loadCompareFields(): Promise<void> {
  const catalog = await fetchModels()
  const seen = new Map<string, string>()
  for (const model of catalog) {
    for (const variable of model.variables) {
      if (!seen.has(variable.id)) seen.set(variable.id, variable.label)
    }
  }
  compare.fields = [...seen].map(([id, label]) => ({ id, label }))
  if (compare.fields.length > 0 && !seen.has(compare.field)) {
    compare.field = compare.fields[0].id
  }
}

// Assigning to `$state` stores a proxy, so nothing here may compare bodies by
// identity; staleness is a counter, the same way `location.select` tracks it.
let requestSeq = 0
let lastKey = ''

/**
 * Fetch unless this exact question has already been asked.
 *
 * The key carries the catalog `revision` — "a run landed" — and deliberately
 * not `polled`, which means "a cycle boundary moved" and changes nothing that
 * is drawn. Refetching five series once a minute forever is the failure that
 * distinction was built for.
 */
export async function ensureCompare(
  lat: number,
  lon: number,
  revision: number,
  field: string = compare.field,
): Promise<void> {
  const key = `${lat.toFixed(4)},${lon.toFixed(4)}|${field}|${revision}`
  if (key === lastKey) return
  lastKey = key

  const seq = ++requestSeq
  compare.status = 'loading'
  compare.error = null
  try {
    const body = await fetchCompare(lat, lon, field)
    if (seq !== requestSeq) return
    compare.body = body
    compare.status = 'ready'
  } catch (e) {
    if (isAbort(e)) return // Superseded; the newer request owns the state now.
    if (seq !== requestSeq) return
    // The previous chart stays on screen with an error line, the way
    // `location.select` keeps the last bundle: a stale meteogram with a
    // sentence beats an empty box.
    lastKey = ''
    compare.status = 'error'
    compare.error = e instanceof Error ? e.message : String(e)
    console.warn('compare fetch failed; keeping the previous chart', e)
  }
}

function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}

/** Tests only: the module-level dedupe key outlives a component. */
export function _resetCompare(): void {
  lastKey = ''
  requestSeq = 0
  compare.body = null
  compare.status = 'idle'
  compare.error = null
  compare.hidden = []
  compare.fields = []
  compare.field = 't2m'
  compare.view = 'chart'
}
